import React, { useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import AdvancedScanModal from '../components/AdvancedScanModal';
import SectionBookmarkModal from '../components/SectionBookmarkModal';
import { FiFolder, FiUpload as FiUploadIcon, FiBookmark, FiXCircle } from 'react-icons/fi';
import { MdScanner } from 'react-icons/md';

const UploadView = ({
  file,
  setFile,
  files,
  setFiles,
  handleUpload,
  uploading,
  cancelUpload,
  uploadProgress,
  uploadName,
  uploadIndex,
  uploadTotal,
  uploadOverallProgress,
  uploadEta,
  cancelToken,
  API_URL,
  fieldLabels = [],
  fieldKeys = []
}) => {
  const [advOpen, setAdvOpen] = useState(false);
  const [bookmarkOpen, setBookmarkOpen] = useState(false);
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    onDrop: (acceptedFiles) => setFiles(acceptedFiles),
    multiple: true,
  });
  const singleInputRef = useRef(null);

  return (
    <div>
      <h2>Upload/Scan PDF(s)</h2>

      <div style={{
        backgroundColor: 'var(--hover-bg)',
        color: 'var(--text)',
        padding: '15px',
        borderRadius: '8px',
        fontSize: '16px',
        marginTop: '20px',
        fontWeight: 'bold',
        border: '1px solid var(--border)'
      }}>
        You can upload a single PDF by clicking <strong>Browse</strong> or
        drag and drop multiple PDFs into the area below. Then click the <strong>Upload</strong> button to submit.
      </div>

      {/* Single file picker (hidden) + Browse button */}
      <input
        ref={singleInputRef}
        type="file"
        accept="application/pdf"
        onChange={(e) => setFile(e.target.files[0])}
        style={{ display: 'none' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
        <button
          className="btn"
          onClick={() => singleInputRef.current && singleInputRef.current.click()}
          type="button"
          title="Browse for a single PDF"
        >
          <FiFolder />
          <span style={{ marginLeft: 6 }}>Browse</span>
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => setAdvOpen(true)}
          title="Advanced scanning UI"
        >
          <MdScanner />
          <span style={{ marginLeft: 6 }}>Advanced Scan</span>
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => setBookmarkOpen(true)}
          title="Generate a section bookmark sheet"
        >
          <FiBookmark />
          <span style={{ marginLeft: 6 }}>Section Bookmark</span>
        </button>
        {file && <span style={{ color: 'var(--text)' }}>{file.name}</span>}
      </div>

      <div {...getRootProps()} style={{
        marginTop: 20,
        padding: '24px',
        border: '2px dashed var(--border)',
        borderRadius: '8px',
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text)',
        textAlign: 'center',
        cursor: 'pointer'
      }}>
        <input {...getInputProps()} />
        <p style={{ margin: 0 }}>Drag and drop PDFs here, or click to select multiple files</p>
        {files.length > 0 && (
          <div style={{ marginTop: '10px', color: 'var(--primary)' }}>
            <strong>Files to be uploaded:</strong>
            <ul style={{ listStyleType: 'none', padding: 0 }}>
              {files.map((file, index) => (
                <li key={index} style={{ marginBottom: '5px' }}>
                  {file.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <button style={{ marginTop: '20px' }} onClick={handleUpload} className="btn" title="Upload selected PDFs">
        <FiUploadIcon />
        <span style={{ marginLeft: 6 }}>{uploading ? 'Uploading…' : 'Upload'}</span>
      </button>

      {uploading && (
        <>
          <button
            className="btn btn-delete"
            style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center' }}
            onClick={cancelUpload}
            title="Cancel current upload"
          >
            <FiXCircle />
            <span style={{ marginLeft: 6 }}>Cancel Upload</span>
          </button>

          <div style={{ marginTop: '10px' }}>
            {uploadName && (
              <div style={{ marginBottom: '6px', color: 'var(--text)' }}>
                Uploading: <strong>{uploadName}</strong>
                {typeof uploadIndex === 'number' && uploadTotal > 0 && (
                  <span style={{ marginLeft: 8, color: '#888' }}>
                    ({uploadIndex}/{uploadTotal})
                  </span>
                )}
              </div>
            )}
            <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '5px' }}>
              <div style={{
                height: '10px',
                width: `${uploadProgress}%`,
                backgroundColor: '#1e88e5',
                borderRadius: '5px'
              }}></div>
            </div>
            <p style={{ textAlign: 'center', color: '#666' }}>{uploadProgress}%</p>

            {/* Overall batch progress */}
            {uploadTotal > 1 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '5px' }}>
                  <div style={{
                    height: '10px',
                    width: `${uploadOverallProgress}%`,
                    backgroundColor: '#43a047',
                    borderRadius: '5px'
                  }}></div>
                </div>
                <p style={{ textAlign: 'center', color: '#666' }}>
                  Total: {uploadOverallProgress}%{uploadEta ? ` • ETA: ~${uploadEta}` : ''}
                </p>
              </div>
            )}
          </div>

          <div className="upload-spinner-overlay">
            <div className="upload-spinner-box">
              <div className="spinner-large"></div>
              <p>
                {uploadName
                  ? `Uploading ${uploadName}${uploadTotal > 0 ? ` (${uploadIndex}/${uploadTotal})` : ''}${uploadEta ? ` • ETA: ~${uploadEta}` : ''}…`
                  : 'Uploading files, please wait...'}
              </p>
            </div>
          </div>
        </>
      )}
      <AdvancedScanModal open={advOpen} onClose={() => setAdvOpen(false)} API_URL={API_URL} fieldLabels={fieldLabels} fieldKeys={fieldKeys} />
      <SectionBookmarkModal open={bookmarkOpen} onClose={() => setBookmarkOpen(false)} API_URL={API_URL} />
    </div>
  );
};

export default UploadView;
